import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import toast from 'react-hot-toast';
import { 
  Coins, 
  CreditCard, 
  Zap, 
  Star, 
  TrendingUp, 
  Shield, 
  Check, 
  ArrowRight,
  Loader,
  Gift,
  AlertCircle,
  BarChart3,
  Lightbulb
} from 'lucide-react';

const CreditPurchasePage = () => {
  const { user, API_BASE_URL, credits, refreshCredits } = useAuth();
  const [packages, setPackages] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    fetchPackages();
    fetchAnalytics();
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/credits/packages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPackages(data);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      toast.error('Failed to load credit packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/credits/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handlePurchase = async (packageId) => {
    setPurchasing(packageId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/credits/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ package_id: packageId })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to checkout
        window.location.href = data.checkout_url;
      } else {
        throw new Error('Failed to create purchase session');
      }
    } catch (error) {
      console.error('Error creating purchase:', error);
      toast.error('Failed to start purchase. Please try again.');
    } finally {
      setPurchasing(null);
    }
  };

  const getWarningLevel = () => {
    if (credits === 0) return 'out';
    if (credits <= 2) return 'critical';
    if (credits <= 5) return 'low';
    if (credits <= 10) return 'reminder';
    return null;
  };

  const formatPackageName = (packageName) => {
    // Format package names like "thecreatorpackage" to "Creator Package"
    // or "thestarterpackage" to "Starter Package"
    if (!packageName) return packageName;
    
    // Remove "the" prefix if present and split by capital letters
    const cleaned = packageName.replace(/^the/i, '');
    
    // Split camelCase or remove "package" suffix
    const formatted = cleaned
      .replace(/package$/i, '')
      .replace(/([A-Z])/g, ' $1')
      .trim();
    
    // Capitalize first letter of each word
    return formatted
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ') + ' Package';
  };

  const getWarningMessage = () => {
    const level = getWarningLevel();
    switch (level) {
      case 'out':
        return { 
          message: "Out of credits! Purchase credits to continue creating books.", 
          color: 'bg-red-600/30 border-red-600/50 text-red-200',
          icon: AlertCircle
        };
      case 'critical':
        return { 
          message: "Critical: You're almost out of credits!", 
          color: 'bg-red-500/20 border-red-500/30 text-red-300',
          icon: AlertCircle
        };
      case 'low':
        return { 
          message: "Low credits: Consider topping up soon", 
          color: 'bg-yellow-500/20 border-yellow-500/30 text-yellow-300',
          icon: AlertCircle
        };
      case 'reminder':
        return { 
          message: "Reminder: Top up credits to continue creating", 
          color: 'bg-blue-500/20 border-blue-500/30 text-blue-300',
          icon: TrendingUp
        };
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
        <Navbar />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-400"></div>
        </div>
      </div>
    );
  }

  const warning = getWarningMessage();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Power Up Your Creativity
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Get more credits to unlock unlimited AI-powered book creation
          </p>
          
          {/* Current Credits */}
          <div className="inline-flex items-center space-x-3 bg-gray-900/50 px-6 py-4 rounded-2xl border border-purple-500/20">
            <Coins className="h-8 w-8 text-yellow-400" />
            <div className="text-left">
              <p className="text-2xl font-bold text-white">{credits}</p>
              <p className="text-sm text-gray-400">Current Credits</p>
            </div>
          </div>
        </div>

        {/* Warning Banner */}
        {warning && (
          <div className={`mb-8 p-4 rounded-2xl border ${warning.color}`}>
            <div className="flex items-center justify-center space-x-3">
              <warning.icon className="h-6 w-6" />
              <span className="font-semibold">{warning.message}</span>
            </div>
          </div>
        )}

        {/* Usage Analytics */}
        {analytics && (
          <div className="mb-12 bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center space-x-2">
              <BarChart3 className="h-6 w-6 text-purple-400" />
              <span>Your Usage (Last 30 Days)</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-400">{analytics.total_credits_used}</p>
                <p className="text-gray-400">Credits Used</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-400">{analytics.total_credits_purchased}</p>
                <p className="text-gray-400">Credits Purchased</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-400">
                  {analytics.usage_by_operation?.length || 0}
                </p>
                <p className="text-gray-400">Different Operations</p>
              </div>
            </div>
            
            {analytics.recommendations?.suggested_top_up && (
              <div className="mt-4 p-4 bg-blue-500/20 border border-blue-500/30 rounded-lg">
                <p className="text-blue-300 text-center flex items-center justify-center">
                  <Lightbulb className="h-4 w-4 mr-2" />
                  Based on your usage, we recommend the <strong className="ml-1">{formatPackageName(analytics.recommendations.suggested_top_up)}</strong>
                </p>
              </div>
            )}
          </div>
        )}

        {/* Credit Packages */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {Object.entries(packages.packages || {}).map(([packageId, packageInfo]) => (
            <div 
              key={packageId}
              className={`relative bg-gray-900/50 rounded-2xl p-6 border transition-all duration-200 hover:scale-105 ${
                packageInfo.popular 
                  ? 'border-purple-500/50 shadow-lg shadow-purple-500/20' 
                  : 'border-purple-500/20 hover:border-purple-500/40'
              }`}
            >
              {/* Popular Badge */}
              {packageInfo.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center space-x-1">
                    <Star className="h-4 w-4" />
                    <span>Most Popular</span>
                  </div>
                </div>
              )}

              {/* Package Header */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Coins className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{packageInfo.name}</h3>
                <p className="text-gray-400 text-sm">{packageInfo.description}</p>
              </div>

              {/* Credits */}
              <div className="text-center mb-6">
                <p className="text-4xl font-bold text-white mb-2">
                  {packageInfo.credits}
                  <span className="text-lg text-gray-400 ml-1">credits</span>
                </p>
                <p className="text-gray-400 text-sm">
                  Perfect for {Math.floor(packageInfo.credits / 10)} complete books
                </p>
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <p className="text-3xl font-bold text-purple-400">
                  ${packages.current_prices?.[packageId]?.price_usd}
                </p>
                <p className="text-gray-400 text-sm">
                  ${packages.current_prices?.[packageId]?.price_per_credit.toFixed(3)} per credit
                </p>
              </div>

              {/* Features */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center space-x-2">
                  <Check className="h-4 w-4 text-green-400" />
                  <span className="text-gray-300 text-sm">
                    ~{Math.floor(packageInfo.credits / 10)} complete books
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="h-4 w-4 text-green-400" />
                  <span className="text-gray-300 text-sm">
                    ~{Math.floor(packageInfo.credits / 15)} professional covers
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="h-4 w-4 text-green-400" />
                  <span className="text-gray-300 text-sm">
                    ~{Math.floor(packageInfo.credits / 5)} metadata optimizations
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Check className="h-4 w-4 text-green-400" />
                  <span className="text-gray-300 text-sm">Never expires</span>
                </div>
              </div>

              {/* Purchase Button */}
              <button
                onClick={() => handlePurchase(packageId)}
                disabled={purchasing === packageId}
                className={`w-full py-3 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 ${
                  packageInfo.popular
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
                    : 'bg-gray-700 text-white hover:bg-gray-600'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {purchasing === packageId ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    <span>Buy Credits</span>
                  </>
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Security & Trust */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center space-x-6 bg-gray-900/50 px-8 py-4 rounded-2xl border border-purple-500/20">
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-green-400" />
              <span className="text-gray-300 text-sm">Secure Payment</span>
            </div>
            <div className="flex items-center space-x-2">
              <Zap className="h-5 w-5 text-blue-400" />
              <span className="text-gray-300 text-sm">Instant Credits</span>
            </div>
            <div className="flex items-center space-x-2">
              <Gift className="h-5 w-5 text-purple-400" />
              <span className="text-gray-300 text-sm">Never Expires</span>
            </div>
          </div>
          
          <p className="text-gray-500 text-sm mt-4">
            Secure payment processing • Your payment information is encrypted
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreditPurchasePage;